import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";

import {
  approveChartTemplate,
  CvceMarketingError,
  chartTemplateKey,
  chartTemplatesKey,
  cvceMeKey,
  decisionMessage,
  getChartTemplate,
  getChartTemplates,
  getCvceMe,
  messageFor,
  rejectChartTemplate,
  type ChartTemplateItem,
  type ChartTemplateVersion,
} from "./api";
import { formatWhen } from "./format";

// The chart-template registry (Chart Template spec v0.2, D7; UX design
// `…/01-customer-voice-content-engine/specifications/2026-10-21-chart-styles-tab-ux-design.md`).
// cvce owns every state; this tab lists templates and calls approve / reject.

/** TECH & SYSTEMS: Upgrades & Bugs. A shape no primitive can draw is a code change (Non-goal 1). */
export const REQUEST_SHAPE_URL = "https://www.notion.so/1880402fc6c980a4bf58d6748cc7a0a7";

const FILTERS = ["all", "live", "draft", "retired"] as const;
type Filter = (typeof FILTERS)[number];
const FILTER_LABEL: Record<Filter, string> = { all: "All", live: "Live", draft: "Draft", retired: "Retired" };

const STATE_LABEL: Record<string, string> = { draft: "Draft", live: "Live", retired: "Retired" };

export function StateChip({ status }: { status: string }) {
  const variant = status === "live" ? "default" : status === "draft" ? "secondary" : "outline";
  return <Badge variant={variant}>{STATE_LABEL[status] ?? status}</Badge>;
}

const day = (iso: string | null) => (iso ? (formatWhen(iso)?.split(",")[0] ?? iso) : null);

function Thumbnail({ url, name }: { url: string | null; name: string }) {
  return (
    <div className="relative flex aspect-[4/3] w-full items-center justify-center overflow-hidden rounded-md border border-ks-grey-4 bg-ks-grey-5">
      {url ? (
        <>
          <img src={url} alt={`Example chart for ${name}`} className="h-full w-full object-contain" loading="lazy" />
          {/* The previews are drawn from placeholder data, never a real study. */}
          <span className="absolute bottom-1 left-1 rounded bg-white/90 px-1.5 py-0.5 text-[11px] text-ks-grey-1">
            Example data
          </span>
        </>
      ) : (
        <span className="text-xs text-ks-grey-1">No preview</span>
      )}
    </div>
  );
}

/** The card's "what this chart is" title, then its one-line description. */
const cardTitle = (item: ChartTemplateItem) => item.card_title || item.display_name;

function Subheadline({ item }: { item: ChartTemplateItem }) {
  return item.template.description ? <p className="text-sm text-ks-ink">{item.template.description}</p> : null;
}

function claimShape(item: { primitive: string; standard_claim_kind: string }) {
  return item.primitive === item.standard_claim_kind
    ? item.primitive
    : `${item.primitive} · ${item.standard_claim_kind}`;
}

function approvalLine(item: { template_status: string; approved_by: string | null; approved_at: string | null }) {
  if (item.template_status !== "live" || !item.approved_by) return null;
  return `Approved by ${item.approved_by}, ${day(item.approved_at)}`;
}

/** Approve + Reject for one draft version. Rendered only when cvce says the viewer may approve. */
function Decision({ item }: { item: Pick<ChartTemplateItem, "slug" | "version" | "display_name"> }) {
  const client = useQueryClient();
  const [rejecting, setRejecting] = useState(false);
  const [note, setNote] = useState("");
  const [done, setDone] = useState<string | null>(null);
  const settle = () => {
    void client.invalidateQueries({ queryKey: chartTemplatesKey });
  };
  const approve = useMutation({
    mutationFn: () => approveChartTemplate(item.slug, item.version),
    onSuccess: () => {
      setDone("Approved, live in the next config version.");
      settle();
    },
    onError: (error) => {
      if (error instanceof CvceMarketingError && error.status === 409) settle();
    },
  });
  const reject = useMutation({
    mutationFn: () => rejectChartTemplate(item.slug, item.version, note.trim() || null),
    onSuccess: () => {
      setRejecting(false);
      setDone("Rejected. The live version is unchanged.");
      settle();
    },
  });
  const failure = approve.error ?? reject.error;
  const busy = approve.isPending || reject.isPending;

  if (done) return <p className="text-sm text-ks-ink" role="status">{done}</p>;
  return (
    <div className="flex flex-col items-end gap-1">
      <div className="flex gap-2">
        <Button variant="outline" size="sm" disabled={busy} onClick={() => setRejecting(true)}>
          Reject
        </Button>
        <Button size="sm" disabled={busy} onClick={() => approve.mutate()}>
          {approve.isPending ? "Approving…" : "Approve"}
        </Button>
      </div>
      {failure ? (
        <p className="text-sm text-destructive" role="alert">
          {decisionMessage(failure)}
        </p>
      ) : null}
      <AlertDialog open={rejecting} onOpenChange={setRejecting}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reject this draft?</AlertDialogTitle>
            <AlertDialogDescription>
              {item.display_name} v{item.version} is retired. The live version keeps rendering.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Textarea
            aria-label="Note for the proposer (optional)"
            placeholder="Note for the proposer (optional)"
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction disabled={busy} onClick={() => reject.mutate()}>
              Confirm reject
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export function QueueRow({
  item,
  mayApprove,
  onOpen,
}: {
  item: ChartTemplateItem;
  mayApprove: boolean;
  onOpen: (slug: string) => void;
}) {
  return (
    <li className="flex flex-wrap items-start gap-3 border-b border-ks-grey-4 py-3">
      <div className="w-24 shrink-0 sm:w-32">
        <Thumbnail url={item.thumbnail_url} name={item.display_name} />
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-1 break-words">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-semibold text-ks-ink">{cardTitle(item)}</span>
          <span className="text-sm text-ks-grey-1">v{item.version}</span>
          <StateChip status={item.template_status} />
        </div>
        <Subheadline item={item} />
        <p className="text-sm text-ks-grey-1">
          {claimShape(item)} · proposed by {item.drafted_by}, {day(item.drafted_at)}
        </p>
        <p className="text-sm text-ks-ink">
          {item.uses.length === 0
            ? "Not used in a draft yet."
            : `Used in: ${item.uses.map((use) => `${use.asset_id} (${use.variant})`).join(", ")}`}
        </p>
      </div>
      <div className="flex w-full flex-wrap items-start justify-end gap-2 sm:w-auto sm:flex-col sm:items-end">
        <Button variant="ghost" size="sm" onClick={() => onOpen(item.slug)}>
          Details
        </Button>
        {mayApprove ? (
          <Decision item={item} />
        ) : (
          <p className="text-sm text-ks-grey-1">Waiting for Damien's sign-off</p>
        )}
      </div>
    </li>
  );
}

export function LibraryCard({ item, onOpen }: { item: ChartTemplateItem; onOpen: (slug: string) => void }) {
  const approval = approvalLine(item);
  return (
    <Card className="cursor-pointer" onClick={() => onOpen(item.slug)}>
      <CardContent className="flex flex-col gap-2 p-3">
        <Thumbnail url={item.thumbnail_url} name={item.display_name} />
        <button
          type="button"
          className="text-left font-semibold text-ks-ink hover:underline focus-visible:underline"
          onClick={(event) => {
            event.stopPropagation();
            onOpen(item.slug);
          }}
        >
          {cardTitle(item)}
        </button>
        <Subheadline item={item} />
        <p className="text-sm text-ks-grey-1">{claimShape(item)}</p>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <StateChip status={item.template_status} />
          <span className="text-ks-grey-1">
            {item.live_version !== null ? `Live v${item.live_version}` : `v${item.version}`}
          </span>
        </div>
        {approval ? <p className="text-xs text-ks-grey-1">{approval}</p> : null}
      </CardContent>
    </Card>
  );
}

function Line({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex justify-between gap-4 border-b border-ks-grey-4 py-2 text-sm text-ks-ink">
      <span>{label}</span>
      <span className="text-right">{value}</span>
    </div>
  );
}

const listOrNone = (values: string[] | undefined) => (values && values.length > 0 ? values.join(", ") : "None");

function keyValues(record: Record<string, unknown> | undefined) {
  if (!record || Object.keys(record).length === 0) return "None";
  return Object.entries(record)
    .map(([key, value]) => `${key}: ${typeof value === "object" ? JSON.stringify(value) : String(value)}`)
    .join(" · ");
}

function historyLine(version: ChartTemplateVersion) {
  const parts = [`drafted by ${version.drafted_by}, ${day(version.drafted_at)}`];
  if (version.approved_by) parts.push(`approved by ${version.approved_by}, ${day(version.approved_at)}`);
  if (version.rejected_by) {
    const note = version.reject_note ? `: ${version.reject_note}` : "";
    parts.push(`rejected by ${version.rejected_by}, ${day(version.rejected_at)}${note}`);
  }
  return parts.join(" · ");
}

export function TemplateSpecifics({ version, mayApprove }: { version: ChartTemplateVersion; mayApprove: boolean }) {
  const template = version.template;
  return (
    <div className="flex flex-col gap-4">
      <Thumbnail url={version.thumbnail_url} name={version.display_name} />
      {template.description ? <p className="text-sm text-ks-ink">{template.description}</p> : null}
      {version.template_status === "draft" && mayApprove ? <Decision item={version} /> : null}
      <section>
        <h3 className="pb-1 text-sm font-semibold text-ks-ink">Specifics</h3>
        <Line label="Claim shape" value={claimShape(version)} />
        <Line label="Required brief words" value={listOrNone(template.brief?.required)} />
        <Line label="Optional brief words" value={listOrNone(template.brief?.optional)} />
        <Line label="Input limits" value={keyValues(template.input)} />
        <Line label="Layout options" value={keyValues(template.layout)} />
        <Line label="Example asset" value={version.example_asset_id ?? "None"} />
        <Line label="Example verdict" value={version.example_verdict ?? "None"} />
      </section>
      {version.example_brief ? (
        <Collapsible>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm">
              Example brief
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <pre className="overflow-x-auto rounded-md bg-ks-grey-5 p-3 text-xs text-ks-ink">
              {JSON.stringify(version.example_brief, null, 2)}
            </pre>
          </CollapsibleContent>
        </Collapsible>
      ) : null}
    </div>
  );
}

function DetailSheet({
  slug,
  mayApprove,
  areaName,
  onClose,
}: {
  slug: string | null;
  mayApprove: boolean;
  areaName: string;
  onClose: () => void;
}) {
  const detail = useQuery({
    queryKey: chartTemplateKey(slug ?? ""),
    queryFn: () => getChartTemplate(slug ?? ""),
    enabled: slug !== null,
  });
  const versions = detail.data?.data.versions ?? [];
  const latest = versions[versions.length - 1];
  return (
    <Sheet open={slug !== null} onOpenChange={(open) => (open ? undefined : onClose())}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-lg">
        <SheetHeader>
          <SheetTitle>{latest?.display_name ?? slug}</SheetTitle>
          <SheetDescription>
            {latest ? (
              <span className="flex items-center gap-2">
                <StateChip status={latest.template_status} /> v{latest.version}
              </span>
            ) : (
              "Chart template"
            )}
          </SheetDescription>
        </SheetHeader>
        <div className="flex flex-col gap-4 p-4">
          {detail.isLoading ? <Skeleton className="h-64 w-full" /> : null}
          {detail.isError ? (
            <Alert variant="destructive">
              <AlertTitle>Couldn't load this template</AlertTitle>
              <AlertDescription className="flex items-center gap-2">
                {messageFor(detail.error, areaName)}
                <Button variant="outline" size="sm" onClick={() => void detail.refetch()}>
                  Retry
                </Button>
              </AlertDescription>
            </Alert>
          ) : null}
          {latest ? <TemplateSpecifics version={latest} mayApprove={mayApprove} /> : null}
          {versions.length > 0 ? (
            <section>
              <h3 className="pb-1 text-sm font-semibold text-ks-ink">Version history</h3>
              <ol className="flex flex-col gap-1 text-sm text-ks-ink">
                {[...versions].reverse().map((version) => (
                  <li key={version.version} className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold">v{version.version}</span>
                    <StateChip status={version.template_status} />
                    <span className="text-ks-grey-1">{historyLine(version)}</span>
                  </li>
                ))}
              </ol>
            </section>
          ) : null}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export function ChartStylesView({
  templates,
  mayApprove,
  stale,
  onOpen,
}: {
  templates: ChartTemplateItem[];
  mayApprove: boolean;
  stale: string | null;
  onOpen: (slug: string) => void;
}) {
  const [filter, setFilter] = useState<Filter>("all");
  const drafts = templates.filter((item) => item.template_status === "draft");
  const shown = filter === "all" ? templates : templates.filter((item) => item.template_status === filter);
  return (
    <div className="flex flex-col gap-6">
      <div className="flex justify-end">
        <a className="text-sm text-ks-ink underline" href={REQUEST_SHAPE_URL} target="_blank" rel="noreferrer">
          Request a new chart shape
        </a>
      </div>
      {stale ? (
        <Alert>
          <AlertDescription>{stale}</AlertDescription>
        </Alert>
      ) : null}
      {templates.length === 0 ? (
        <p className="text-sm text-ks-grey-1">No chart templates yet.</p>
      ) : null}
      {drafts.length > 0 ? (
        <section aria-labelledby="chart-styles-queue">
          <h2 id="chart-styles-queue" className="text-base font-semibold text-ks-ink">
            Waiting for {mayApprove ? "your" : "Damien's"} sign-off ({drafts.length})
          </h2>
          <ul>
            {drafts.map((item) => (
              <QueueRow key={`${item.slug}@${item.version}`} item={item} mayApprove={mayApprove} onOpen={onOpen} />
            ))}
          </ul>
        </section>
      ) : null}
      {templates.length > 0 ? (
        <section aria-labelledby="chart-styles-library" className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 id="chart-styles-library" className="text-base font-semibold text-ks-ink">
              Library
            </h2>
            <Tabs value={filter} onValueChange={(value) => setFilter(value as Filter)}>
              <TabsList>
                {FILTERS.map((key) => (
                  <TabsTrigger key={key} value={key}>
                    {FILTER_LABEL[key]}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>
          {shown.length === 0 ? (
            <p className="text-sm text-ks-grey-1">No {FILTER_LABEL[filter].toLowerCase()} templates.</p>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {shown.map((item) => (
                <LibraryCard key={item.slug} item={item} onOpen={onOpen} />
              ))}
            </div>
          )}
        </section>
      ) : null}
    </div>
  );
}

export function ChartStylesTab({ areaName }: { areaName: string }) {
  const [open, setOpen] = useState<string | null>(null);
  const list = useQuery({ queryKey: chartTemplatesKey, queryFn: getChartTemplates });
  // Approve is shown only when cvce's own `/me` says so: the same helper its gate uses.
  const me = useQuery({ queryKey: cvceMeKey, queryFn: getCvceMe, retry: false });
  const mayApprove = me.data?.may_approve === true;

  if (list.isLoading) {
    return (
      <div className="flex flex-col gap-4" aria-busy="true">
        <Skeleton className="h-24 w-full" />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Skeleton className="h-56" />
          <Skeleton className="h-56" />
          <Skeleton className="h-56" />
        </div>
      </div>
    );
  }
  if (!list.data) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Couldn't load chart templates.</AlertTitle>
        <AlertDescription className="flex items-center gap-2">
          {messageFor(list.error, areaName)}
          <Button variant="outline" size="sm" onClick={() => void list.refetch()}>
            Retry
          </Button>
        </AlertDescription>
      </Alert>
    );
  }
  const stale = list.isError
    ? `Showing data from ${formatWhen(new Date(list.dataUpdatedAt).toISOString())}; refresh failed.`
    : null;
  return (
    <>
      <ChartStylesView templates={list.data.data.templates} mayApprove={mayApprove} stale={stale} onOpen={setOpen} />
      <DetailSheet slug={open} mayApprove={mayApprove} areaName={areaName} onClose={() => setOpen(null)} />
    </>
  );
}
